/**
 * User-chosen display timezone for Recents, Messages, and Voicemail.
 * Stored locally so any signed-in person can set "the zone I am in"
 * without changing a line's business hours.
 */
import { useEffect, useState } from 'react';
import { readStored, writeStored } from './storageKeys';

export const DISPLAY_TIMEZONE_KEY = 'truva_display_timezone';
export const DISPLAY_TIMEZONE_EVENT = 'truva-timezone-changed';

export const DISPLAY_TIMEZONES = [
  { id: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { id: 'America/Phoenix', label: 'Arizona (Phoenix, no DST)' },
  { id: 'America/Denver', label: 'Mountain (Denver)' },
  { id: 'America/Chicago', label: 'Central (Chicago)' },
  { id: 'America/New_York', label: 'Eastern (New York)' },
  { id: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { id: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
  { id: 'America/Puerto_Rico', label: 'Atlantic (Puerto Rico)' },
  { id: 'UTC', label: 'UTC' },
];

export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_) {
    return '';
  }
}

export function isValidTimeZone(name) {
  if (!name || typeof name !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name }).format(new Date());
    return true;
  } catch (_) {
    return false;
  }
}

export function timezoneChoices(current) {
  const seen = new Set(DISPLAY_TIMEZONES.map((z) => z.id));
  const extra = [];
  const browser = browserTimeZone();
  if (browser && !seen.has(browser) && isValidTimeZone(browser)) {
    extra.push({ id: browser, label: `Device (${browser})` });
    seen.add(browser);
  }
  if (current && !seen.has(current) && isValidTimeZone(current)) {
    extra.push({ id: current, label: current });
  }
  return [...extra, ...DISPLAY_TIMEZONES];
}

export function loadDisplayTimeZone() {
  const stored = (readStored(DISPLAY_TIMEZONE_KEY) || '').trim();
  if (isValidTimeZone(stored)) return stored;
  const browser = browserTimeZone();
  if (isValidTimeZone(browser)) return browser;
  return 'America/Los_Angeles';
}

export function saveDisplayTimeZone(name) {
  const tz = isValidTimeZone(name) ? name : loadDisplayTimeZone();
  writeStored(DISPLAY_TIMEZONE_KEY, tz);
  try {
    window.dispatchEvent(new CustomEvent(DISPLAY_TIMEZONE_EVENT, { detail: tz }));
  } catch (_) { /* ignore */ }
  return tz;
}

/** Treat naive API timestamps as UTC (calls.timestamp is UTC wall-clock). */
export function parseUtcDate(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return Number.isNaN(ts.getTime()) ? null : ts;
  const s = String(ts).trim();
  if (!s) return null;
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(`${s.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function zonedParts(date, timeZone) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  const map = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }
  return map;
}

export function formatInTimeZone(ts, timeZone, style = 'datetime') {
  const date = parseUtcDate(ts);
  if (!date) return '';
  const tz = isValidTimeZone(timeZone) ? timeZone : loadDisplayTimeZone();
  const opts = { timeZone: tz };
  if (style === 'time') {
    return date.toLocaleTimeString([], { ...opts, hour: 'numeric', minute: '2-digit' });
  }
  if (style === 'date') {
    return date.toLocaleDateString([], { ...opts, month: 'short', day: 'numeric' });
  }
  return date.toLocaleString([], opts);
}

export function formatRelativeInTimeZone(ts, timeZone, now = new Date()) {
  const date = parseUtcDate(ts);
  if (!date) return '';
  const tz = isValidTimeZone(timeZone) ? timeZone : loadDisplayTimeZone();
  const diffMins = Math.floor((now - date) / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;

  const a = zonedParts(date, tz);
  const b = zonedParts(now, tz);
  const timeStr = formatInTimeZone(date, tz, 'time');
  if (a.year === b.year && a.month === b.month && a.day === b.day) {
    return `Today, ${timeStr}`;
  }
  const yest = new Date(now.getTime() - 86400000);
  const y = zonedParts(yest, tz);
  if (a.year === y.year && a.month === y.month && a.day === y.day) {
    return `Yesterday, ${timeStr}`;
  }
  return `${formatInTimeZone(date, tz, 'date')}, ${timeStr}`;
}

export function useDisplayTimeZone() {
  const [tz, setTz] = useState(loadDisplayTimeZone);
  useEffect(() => {
    const refresh = () => setTz(loadDisplayTimeZone());
    window.addEventListener(DISPLAY_TIMEZONE_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(DISPLAY_TIMEZONE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);
  return tz;
}
